import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HeaderComponent } from './components/header/header.component';
import { ConfigurationDetailsComponent } from './components/configuration/configuration-details/configuration-details.component';
import { ConfigurationListComponent } from './components/configuration/configuration-list/configuration-list.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { CookieService } from 'ngx-cookie-service';

import { MAT_SELECT_CONFIG, MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatListModule } from '@angular/material/list';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';

import { ComponentListComponent } from './components/component/component-list/component-list.component';
import { ReleaseOverviewComponent } from './components/release/release-overview/release-overview.component';
import { ManageBikesComponent } from './components/manage-bikes/manage-bikes.component';
import { ReleaseDetailsComponent } from './components/release/release-details/release-details.component';
import { ConfirmationDialogComponent } from './components/modals/confirmation-dialog/confirmation-dialog.component';
import { LoginComponent } from './components/login/login.component';
import { SearchPipe } from './pipes/search.pipe';
import { DndDirective } from './directives/dnd.directive';
import { LoadingAnimationComponent } from './components/loading-animation/loading-animation.component';
import { BikeDetailsComponent } from './components/manage-bikes/bike-details/bike-details.component';
import { SearchPropertyPipe } from './pipes/search-property.pipe';

import { MsalGuard, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MsalRedirectComponent, MsalInterceptor, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';

import { MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from './auth-config';
import { NumberInputDirective } from './directives/number-input.directive';
import { ListFieldsSearchByPipe } from './pipes/list-fields-search-by.pipe';
import { ErrorDialogComponent } from './components/modals/error-dialog/error-dialog.component';
import { AccountService } from './services/account.service';
import { AccountHeaderComponent } from './components/account-header/account-header.component';
import { RecalledListComponent } from './components/component/recalled-list/recalled-list.component';
import { RecalledUploadComponent } from './components/component/recalled-upload/recalled-upload.component';
import { ClipboardModule } from 'ngx-clipboard';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgxPaginationModule } from 'ngx-pagination';
import { DatePipe } from '@angular/common';
import { ErrorCatchingInterceptor } from './interceptors/error-catching.interceptor';
import { OrderByPipe } from './pipes/order-by.pipe';
import { MatNativeDateModule } from '@angular/material/core';
import { MultipleSearchPropertyPipe } from './pipes/multiple-search-property.pipe';
import { MultipleSearchComponent } from './components/ui-panels/multiple-search/multiple-search.component';
import { SearchBadgeComponent } from './components/ui-panels/search-badge/search-badge.component';
import { UnassignedBikesComponent } from './components/unassigned-bikes/unassigned-bikes.component';
import { StartApprovalRolloutComponent } from './components/rollout/start-approval-rollout/start-approval-rollout.component';
import { StartRolloutComponent } from './components/rollout/start-rollout/start-rollout.component';
import { AddRuleComponent } from './components/rollout/add-rule/add-rule.component';
import { InputLengthDirective } from './directives/input-length.directive';
import { MonitorSoftwareComponent } from './components/rollout/monitor-software/monitor-software.component';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, registerables } from 'chart.js';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AddSubComponentComponent } from './components/component/add-sub-component/add-sub-component.component';
import { BulkUploadResultDialogComponent } from './components/modals/bulk-upload-result-dialog/bulk-upload-result-dialog.component';
Chart.register(...registerables);

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    ConfigurationDetailsComponent,
    ConfigurationListComponent,
    ComponentListComponent,
    ReleaseOverviewComponent,
    ManageBikesComponent,
    ReleaseDetailsComponent,
    ConfirmationDialogComponent,
    LoginComponent,
    SearchPipe,
    DndDirective,
    LoadingAnimationComponent,
    BikeDetailsComponent,
    SearchPropertyPipe,
    NumberInputDirective,
    ListFieldsSearchByPipe,
    OrderByPipe,
    MultipleSearchPropertyPipe,
    ErrorDialogComponent,
    AccountHeaderComponent,
    RecalledListComponent,
    RecalledUploadComponent,
    MultipleSearchComponent,
    SearchBadgeComponent,
    UnassignedBikesComponent,
    StartApprovalRolloutComponent,
    StartRolloutComponent,
    AddRuleComponent,
    InputLengthDirective,
    MonitorSoftwareComponent,
    AddSubComponentComponent,
    BulkUploadResultDialogComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    MatSelectModule,
    MatCheckboxModule,
    MatListModule,
    MatDialogModule,
    MatSnackBarModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatMenuModule,
    MatIconModule,
    HttpClientModule,
    DragDropModule,
    MsalModule,
    ClipboardModule,
    NgScrollbarModule,
    NgxPaginationModule,
    MatNativeDateModule,
    BaseChartDirective
  ],
  providers: [
    CookieService,
    SearchPropertyPipe,
    MultipleSearchPropertyPipe,
    DatePipe,
    OrderByPipe,
    AccountService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorCatchingInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { hideSingleSelectionIndicator: true }
    },
    MsalService,
    MsalGuard],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule { }
